/**
 * Ritual Group Chat Hooks
 * React Query hooks for ritual organizer group chat
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  createGroupChat,
  deleteMessage,
  editMessage,
  getGroupChat,
  getMessages,
  getOrganizerChatByRitualId,
  listGroupChats,
  markAsRead,
  sendMessage,
  startExpertGroupChat,
} from "../services/ritual-group-chat.service"

export function useRitualGroupChats(page = 1, limit = 20) {
  return useQuery({
    queryKey: ["ritual-group-chats", page, limit],
    queryFn: () => listGroupChats(page, limit),
    staleTime: 30 * 1000,
  })
}

export function useRitualGroupChat(id: string | undefined) {
  return useQuery({
    queryKey: ["ritual-group-chat", id],
    queryFn: () => getGroupChat(id as string),
    enabled: !!id,
    staleTime: 10 * 1000,
  })
}

export function useOrganizerChatByRitualId(ritualId: string | undefined) {
  return useQuery({
    queryKey: ["organizer-chat-by-ritual", ritualId],
    queryFn: () => getOrganizerChatByRitualId(ritualId as string),
    enabled: !!ritualId,
    staleTime: 30 * 1000,
  })
}

export function useRitualGroupMessages(groupChatId: string | undefined, page = 1, limit = 50) {
  return useQuery({
    queryKey: ["ritual-group-messages", groupChatId, page, limit],
    queryFn: () => getMessages(groupChatId as string, page, limit),
    enabled: !!groupChatId,
    staleTime: 10 * 1000,
  })
}

export function useCreateRitualGroupChat() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (ritualId: string) => createGroupChat(ritualId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ritual-group-chats"] })
    },
  })
}

/**
 * Create or get the per-booking expert group chat from "Chat with Expert".
 * On success, invalidates both the ritual-group-chats list (where this row
 * lives) and the unified all-conversations list.
 */
export function useStartExpertGroupChat() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (opts: { bookingId?: string; ritualId?: string }) => startExpertGroupChat(opts),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ritual-group-chats"] })
      queryClient.invalidateQueries({ queryKey: ["all-conversations"] })
    },
  })
}

export function useSendRitualGroupMessage() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ groupChatId, content }: { groupChatId: string; content: string }) =>
      sendMessage(groupChatId, content),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["ritual-group-messages", variables.groupChatId],
      })
      queryClient.invalidateQueries({ queryKey: ["ritual-group-chats"] })
    },
  })
}

export function useEditRitualGroupMessage() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      groupChatId,
      messageId,
      content,
    }: {
      groupChatId: string
      messageId: string
      content: string
    }) => editMessage(groupChatId, messageId, content),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["ritual-group-messages", variables.groupChatId],
      })
    },
  })
}

export function useDeleteRitualGroupMessage() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ groupChatId, messageId }: { groupChatId: string; messageId: string }) =>
      deleteMessage(groupChatId, messageId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["ritual-group-messages", variables.groupChatId],
      })
      queryClient.invalidateQueries({ queryKey: ["ritual-group-chats"] })
    },
  })
}

export function useMarkRitualGroupAsRead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (groupChatId: string) => markAsRead(groupChatId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ritual-group-chats"] })
    },
  })
}
